import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { accountLabel, fmtMoney } from "@/lib/format";
import { Loader2, RotateCcw, Sparkles, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const bc = { borderColor: "var(--ll-border)" };

export function ChainFixAllBar({ issues, accounts, canFix, onApplied }) {
  const [openAcc, setOpenAcc] = useState(null);
  const counts = {};
  issues.filter((i) => i.type === "break").forEach((i) => { counts[i.bank_account_id] = (counts[i.bank_account_id] || 0) + 1; });
  const ids = Object.keys(counts);
  if (!canFix || !ids.length) return null;
  return (
    <div className="mb-5 p-4 border flex flex-wrap items-center gap-3" style={{ background: "#FFF7ED", borderColor: "#F59E0B" }} data-testid="chain-fix-all-bar">
      <span className="text-sm text-[#B45309] font-medium">Chain breaks:</span>
      {ids.map((id) => (
        <button key={id} onClick={() => setOpenAcc(id)} data-testid={`chain-fix-all-${id}`}
          className="text-xs px-3 py-1.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1.5 transition-colors" style={bc}>
          <Sparkles size={12} /> {counts[id] === 1 ? "Fix 1 break" : `Fix all ${counts[id]} breaks`} in {accountLabel(accounts.find((a) => a.id === id))} with AI
        </button>
      ))}
      {openAcc && <FixAllDialog accountId={openAcc} label={accountLabel(accounts.find((a) => a.id === openAcc))}
        onClose={() => setOpenAcc(null)} onApplied={onApplied} />}
    </div>
  );
}

function FixAllDialog({ accountId, label, onClose, onApplied }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const load = () => {
    setData(null);
    return api.post("/statements/chain-diagnose-all", { bank_account_id: accountId }).then((r) => {
      setData(r.data);
      setPicked(new Set(r.data.items.flatMap((i) => i.fixes.map((f) => f.id))));
    }).catch((err) => { toast.error(err?.response?.data?.detail || "Diagnosis failed"); onClose(); });
  };
  const started = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!started.current) { started.current = true; load(); } }, []);

  const run = async (groups, isUndo) => {
    setBusy(true);
    const undo = [];
    let fixed = 0;
    try {
      for (const g of groups) {
        const r = await api.post(`/statements/${g.statement_id}/chain-fix`, { fixes: g.fixes });
        undo.push({ statement_id: g.statement_id, fixes: r.data.undo });
        if (["ok", "first"].includes(r.data.chain_status)) fixed += 1;
      }
      if (isUndo) { toast.success("All changes undone"); setDone(null); await load(); }
      else setDone({ undo: undo.reverse().filter((u) => u.fixes.length), fixed, total: groups.length });
      onApplied();
    } catch (err) { toast.error(err?.response?.data?.detail || "Some fixes failed"); onApplied(); }
    finally { setBusy(false); }
  };
  const applyAll = () => run(data.items.map((i) => ({ statement_id: i.statement_id, fixes: i.fixes.filter((f) => picked.has(f.id)) }))
    .filter((g) => g.fixes.length), false);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="chain-fix-all-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles size={16} /> Fix all chain breaks · {label}</DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]">The AI re-reads every statement involved in a break. Review the suggested fixes, untick any you don't want, then apply them all.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-10 grid place-items-center gap-2 text-sm text-[var(--ll-ink-soft)]" data-testid="chain-fix-all-loading"><Loader2 className="animate-spin" /> Checking every break… this can take a minute.</div>
        ) : done ? (
          <div className="space-y-4" data-testid="chain-fix-all-result">
            <div className="p-3 text-sm border-l-4" style={{ background: "#ECFDF5", borderColor: "#059669" }}>Applied fixes on {done.total} statement{done.total === 1 ? "" : "s"}. {done.fixed} break{done.fixed === 1 ? " now connects" : "s now connect"}.</div>
            <div className="flex gap-2">
              <button onClick={() => run(done.undo, true)} disabled={busy || !done.undo.length} className="flex-1 px-4 py-2 border text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40" style={bc} data-testid="chain-fix-all-undo">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Undo all
              </button>
              <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-white bg-[var(--ll-ink)]" data-testid="chain-fix-all-done">Done</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {data.items.length === 0 && <div className="text-sm" data-testid="chain-fix-all-none">No chain breaks left in this account.</div>}
            {data.items.map((it) => (
              <div key={it.statement_id} className="border p-3" style={bc} data-testid={`chain-fix-all-item-${it.statement_id}`}>
                <div className="text-sm font-medium mb-1">{it.file_name} <span className="font-mono-fin text-xs text-[var(--ll-error)]">off by {fmtMoney(it.difference)}</span></div>
                <ul className="text-xs list-disc pl-5 space-y-1 text-[var(--ll-ink-soft)]">{it.findings.map((f, i) => <li key={i}>{f}</li>)}</ul>
                {it.fixes.map((f) => (
                  <label key={f.id} className="flex items-start gap-2 mt-2 text-sm cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={picked.has(f.id)} onChange={() => toggle(f.id)} data-testid={`chain-fix-all-option-${f.id}`} /> {f.label}
                  </label>
                ))}
                {!it.fixes.length && <div className="text-[11px] text-[var(--ll-ochre)] mt-2">No automatic fix. See the notes above.</div>}
              </div>
            ))}
            <div className="flex gap-2 sticky bottom-0 bg-white pt-2">
              <button onClick={onClose} className="flex-1 px-4 py-2 border text-sm" style={bc} data-testid="chain-fix-all-cancel">Close</button>
              <button onClick={applyAll} disabled={busy || !picked.size} data-testid="chain-fix-all-apply"
                className="flex-1 px-4 py-2 text-sm text-white bg-[var(--ll-ink)] inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Apply all {picked.size} fix{picked.size === 1 ? "" : "es"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
